import { DefaultQueryParams } from '@frontend/api-utils';
import { ChartClient } from '@frontend/chart/api';
import { NivalizTransactionVolumeLineChart } from '@frontend/chart/components';
import { Chart, ChartBinSize, ChartQueryParam } from '@frontend/chart/types';
import { CommonMessage } from '@frontend/lang';
import { TransactionStateName } from '@frontend/transaction/types';
import { useEffect, useMemo, useState } from 'react';

import { getLast30DaysChartRange } from '../last-30-days-chart-range';
import { Last30DaysNivalizTransactionVolumeLineChartProps } from './last-30-days-nivaliz-transaction-volume-line-chart.component';

export const useLast30DaysNivalizTransactionVolumeLineChart = (props: Last30DaysNivalizTransactionVolumeLineChartProps) => {
    const [data, setData] = useState<Chart | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!props.accountId) {
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);

        const { after, before } = getLast30DaysChartRange();

        ChartClient.fetchChartData(props.accountId, NivalizTransactionVolumeLineChart.definition, {
            [DefaultQueryParams.AFTER]: after,
            [DefaultQueryParams.BEFORE]: before,
            [ChartQueryParam.BIN_SIZE]: ChartBinSize.DAY,
            [ChartQueryParam.STATE]: TransactionStateName.TRANSACTION_COMPLETED
        })
            .then((chart) => {
                if (!cancelled) {
                    setData(chart);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setData(undefined);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [props.accountId]);

    const total = useMemo(() => {
        if (!data) {
            return 0;
        }

        return data.series.flatMap((series) => series.data).reduce((sum, point) => sum + point.value, 0);
    }, [data]);

    return {
        data,
        isLoading,
        subtitle: CommonMessage.STATISTICS.COUNT_IN_LAST_30_DAYS(total),
        navigateUrl: props.navigateUrl
    };
};
